/**
 * AttackArrow — chalk-arrow SVG fragment showing the attacker's intent
 * line from the source territory's centroid to the target territory's
 * centroid.
 *
 * Two render modes:
 *
 *   - `mode="committed"`: thick wax-red line, animates `stroke-dashoffset`
 *     so it draws itself in ~400ms. Re-keys on `targetId` so re-selecting
 *     a different target redraws the animation.
 *   - `mode="preview"`: thinner cream line, no draw animation, used while
 *     the user is hovering a valid target during `sourceSelected`. The
 *     idea is "if I click here, I'd be attacking from there to here".
 *
 * Rendered as a `<g>` inside the map's existing `<svg>` (so it shares the
 * same viewBox / zoom transform as the territories).
 *
 * Layered ABOVE the territory polygons but BELOW the cube badges, so the
 * arrow doesn't obscure the cube counts at either endpoint.
 */

import { type ReactNode } from 'react';
import { surfaces } from '@qd/design-tokens';

export type AttackArrowMode = 'committed' | 'preview';

interface AttackArrowProps {
  readonly source: { readonly x: number; readonly y: number };
  readonly target: { readonly x: number; readonly y: number };
  readonly mode: AttackArrowMode;
  /** Used as a React key by the caller so re-selection redraws. */
  readonly arrowKey?: string;
}

/** Arc-back away from the target centroid by this many units so the
 *  arrowhead doesn't bury itself under the cube badge (radius ~12). */
const TARGET_BACKOFF = 16;
/** Same idea on the source end so the line doesn't start from inside the badge. */
const SOURCE_BACKOFF = 14;

export function AttackArrow({ source, target, mode }: AttackArrowProps): ReactNode {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return null;

  const ux = dx / length;
  const uy = dy / length;
  const x1 = source.x + ux * SOURCE_BACKOFF;
  const y1 = source.y + uy * SOURCE_BACKOFF;
  const x2 = target.x - ux * TARGET_BACKOFF;
  const y2 = target.y - uy * TARGET_BACKOFF;

  const visibleLength = Math.hypot(x2 - x1, y2 - y1);

  const isCommitted = mode === 'committed';
  const stroke = isCommitted ? surfaces.waxRed : surfaces.paper;
  const strokeWidth = isCommitted ? 3.2 : 2.2;
  const strokeOpacity = isCommitted ? 0.92 : 0.6;
  const markerId = isCommitted
    ? 'qd-attack-arrow-head-committed'
    : 'qd-attack-arrow-head-preview';

  return (
    <g pointerEvents="none" data-layer="attack-arrow">
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path
            d="M0,0 L10,5 L0,10 Z"
            fill={stroke}
            fillOpacity={strokeOpacity}
          />
        </marker>
      </defs>

      {/* Soft halo behind the line so it reads on busy continents. */}
      {isCommitted ? (
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={surfaces.waxRed}
          strokeWidth={9}
          strokeOpacity={0.18}
          strokeLinecap="round"
        />
      ) : null}

      {/* The arrow itself. */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        strokeLinecap="round"
        strokeDasharray={isCommitted ? `${visibleLength}` : '6 5'}
        strokeDashoffset={isCommitted ? visibleLength : 0}
        markerEnd={`url(#${markerId})`}
      >
        {isCommitted ? (
          <animate
            attributeName="stroke-dashoffset"
            from={visibleLength}
            to={0}
            dur="0.42s"
            fill="freeze"
          />
        ) : null}
      </line>
    </g>
  );
}
